import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { mailboxSubscriptionService } from '@/lib/mailbox-subscription-service'
import { smartleadAPI } from '@/lib/smartlead-api'

export interface SmartleadSyncStatus {
  total_mailboxes: number
  success_count: number
  failure_count: number
  pending_count: number
  last_sync_time: string | null
  sync_initialized: boolean
  failed_mailboxes: Array<{
    email: string
    error: string
    attempt_count: number
    last_attempted: string
  }>
}

export function useSmartleadSyncStatus() {
  const queryClient = useQueryClient()

  // Use centralized subscription service
  useEffect(() => {
    mailboxSubscriptionService.setQueryClient(queryClient)
    mailboxSubscriptionService.subscribe('useSmartleadSyncStatus')

    return () => {
      mailboxSubscriptionService.unsubscribe('useSmartleadSyncStatus')
    }
  }, [queryClient])

  return useQuery({
    queryKey: ['smartlead-sync-status'],
    queryFn: async (): Promise<SmartleadSyncStatus> => {
      // Check for demo mode first
      if (!smartleadAPI.isConfigured()) {
        console.log('🔑 [useSmartleadSyncStatus] Smartlead API not configured, returning demo sync status...')
        
        // Return impressive demo sync status (matching Phase 1 dashboard numbers)
        const demoStatus: SmartleadSyncStatus = {
          total_mailboxes: 156, // Match Phase 1 dashboard
          success_count: 142, // Match Phase 1 dashboard (healthy mailboxes)
          failure_count: 8,
          pending_count: 6,
          last_sync_time: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
          sync_initialized: true,
          failed_mailboxes: [
            {
              email: 'old-mailbox@legacy-domain.com',
              error: 'Domain not found in Smartlead',
              attempt_count: 3,
              last_attempted: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
            }
          ]
        }
        
        console.log('📊 Demo: Loaded sync status with', demoStatus.success_count, 'successful syncs')
        return demoStatus
      }

      console.log('Fetching Smartlead sync status...')
      
      const { data: mailboxes, error } = await supabase
        .from('mailboxes')
        .select(`
          email,
          api_sync_status,
          last_api_synced_at,
          sync_attempted_at,
          sync_attempt_count,
          api_sync_error
        `)
        .order('last_api_synced_at', { ascending: false, nullsFirst: false })

      if (error) {
        console.error('Error fetching mailbox sync status:', error)
        throw error
      }

      console.log('Fetched mailbox sync data:', mailboxes)

      const total_mailboxes = mailboxes?.length || 0
      const success_count = mailboxes?.filter(m => m.api_sync_status === 'SUCCESS').length || 0
      const failure_count = mailboxes?.filter(m => m.api_sync_status === 'FAILURE').length || 0
      const pending_count = mailboxes?.filter(m => 
        m.api_sync_status === 'PENDING' || 
        m.api_sync_status === 'NOT_IN_SMARTLEAD' ||
        !m.api_sync_status
      ).length || 0
      
      // Get the most recent successful sync time
      const last_sync_time = mailboxes?.find(m => m.last_api_synced_at)?.last_api_synced_at || null
      
      // Check if sync has been initialized (any mailbox has been attempted)
      const sync_initialized = mailboxes?.some(m => 
        m.sync_attempted_at || 
        m.last_api_synced_at || 
        m.api_sync_status
      ) || false

      // Get failed mailboxes with details
      const failed_mailboxes = mailboxes?.filter(m => m.api_sync_status === 'FAILURE').map(m => ({
        email: m.email,
        error: m.api_sync_error || 'Unknown error',
        attempt_count: m.sync_attempt_count || 0,
        last_attempted: m.sync_attempted_at || ''
      })) || []

      return {
        total_mailboxes,
        success_count,
        failure_count,
        pending_count,
        last_sync_time,
        sync_initialized,
        failed_mailboxes
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 1 * 60 * 1000, // Refresh every 1 minute
    refetchIntervalInBackground: true, // Keep refetching even when tab is not active
  })
}